<template>
  <fieldset
    class="q-toggle__wrapper flex items-center text-sm"
    :class="minWidth && `min-w-[${minWidth}px]`"
    @input.stop
  >
    <legend v-if="label" class="text-gray-70 mb-1 text-xs font-medium">{{ label }}</legend>

    <div
      class="q-toggle font-medium"
      :class="[
        {
          'q-toggle--condensed': condensed,
          'q-toggle-dark': dark,
          'q-toggle-dark-text': darkText,
          'q-toggle--transparent': transparent,
          'q-toggle--fill-width': fillWidth,
        },
      ]"
    >
      <template v-for="(option, i) in options" :key="i">
        <input
          :id="`${id}-${i}`"
          v-model="val"
          class="q-toggle__input sr-only"
          :name="name || inputName"
          :value="option.value"
          type="radio"
        />
        <label
          :for="`${id}-${i}`"
          class="q-toggle__label q-toggle__label--left whitespace-nowrap leading-none"
          :class="{
            'q-toggle-dark__label': dark,
            'text-xs': condensed,
            'text-sm': !condensed,
          }"
        >
          <component :is="option.icon" v-if="option.icon" class="icon-m mr-1" />
          {{ option.label }}
          <div v-if="'count' in option" class="text-xs font-medium" :class="$style.count">
            <span>{{ option.count }}</span>
          </div>
        </label>
      </template>
    </div>
  </fieldset>
</template>

<script lang="ts">
import { defineComponent, computed, watch, ref, type PropType } from 'vue'
import { v4 as uuid } from 'uuid'
import { sanitize } from '@/utils/sanitize'

interface Option {
  label: string
  value: string | boolean
  icon?: any
  count?: number
}

export default defineComponent({
  name: 'QToggle',
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    name: String,
    label: String,
    modelValue: [String, Boolean],
    condensed: Boolean,
    options: Array as PropType<Option[]>,
    dark: Boolean,
    darkText: Boolean,
    transparent: Boolean,
    fillWidth: Boolean,
    minWidth: Number,
  },
  setup(props, { emit }) {
    const id = uuid()
    const firstOption = props.options?.length ? props.options[0] : null
    const val = ref<string | boolean | undefined>(props.modelValue || firstOption?.value)
    const inputName = computed(() => `q-toggle-${id}`)

    watch(val, () => {
      emit('update:modelValue', typeof val.value === 'string' ? sanitize(val.value) : val.value)
    })

    watch(
      () => props.modelValue,
      () => {
        if (val.value !== props.modelValue) val.value = props.modelValue
      },
      { immediate: true },
    )

    return {
      id,
      val,
      inputName,
    }
  },
})
</script>

<style lang="scss">
.q-toggle {
  display: flex;
  align-items: stretch;
  height: 2.5rem;
  background-color: var(--gray-20);
  border: 1px solid var(--gray-30);
  border-radius: var(--border-radius);
  color: var(--gray-60);
  overflow: hidden;
  user-select: none;

  &__wrapper {
    border: none;
  }

  &__label {
    display: flex;
    align-items: center;
    flex: 1;
    padding: 0 1rem;
    transition: all 0.2s;

    &:hover {
      color: var(--active-primary);
    }

    &--left {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    &--right {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
  &__input:checked + .q-toggle__label {
    background-color: var(--white);
    color: var(--active-primary);
    cursor: unset;
  }

  &--condensed {
    height: 2rem;

    .q-toggle__label {
      padding: 0 0.75rem;
      border-color: var(--gray-30);
    }
  }

  &--transparent {
    background: none;
    border: none;
  }

  &--transparent &__label {
    padding-left: 0;
  }

  &--transparent &__input:checked + &__label {
    background: none;
  }
}

.q-toggle-dark-text {
  .q-toggle__label {
    color: var(--gray-60);

    &:hover {
      color: var(--gray-80);
    }
  }

  .q-toggle__input:checked + .q-toggle__label {
    color: var(--gray-80);

    &:hover {
      color: var(--gray-80);
    }
  }
}

.q-toggle-dark {
  background-color: var(--gray-80);
  border: none;
  color: var(--gray-50);

  &__label {
    &:hover {
      color: var(--white);
    }
  }
}

.q-toggle--fill-width {
  width: 100%;
  .q-toggle__label {
    justify-content: center;
  }
}
</style>

<style lang="scss" module>
:global(.q-toggle__input:checked + .q-toggle__label) {
  .count {
    color: var(--active-primary);
    background-color: var(--active-secondary);
  }
}

.count {
  height: 16px;
  min-width: 16px;
  margin-left: 4px;
  background-color: var(--white);
  color: var(--gray-50);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}
</style>
